import {
  ArrowLeft,
  BookOpenText,
  Check,
  FileAudio,
  FileText,
  FolderKanban,
  PenLine,
  Plus,
  Sparkles,
  Trash2,
  UserCheck,
  UsersRound,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type Entity, type EntityDossier, type EntityMention } from "@/lib/api";
import { BeliefsEditor } from "@/components/BeliefEditor";
import { RelationsPanel } from "@/components/RelationsPanel";
import { cn } from "@/lib/cn";
import { entityIcon, entityTypeLabel } from "@/lib/entityTypes";
import { formatDate } from "@/lib/format";

/**
 * UI-11 — one entity in full: its inline-editable name and description, its aliases, and
 * every place it was mentioned. For a person it also carries the "is me" link (SPCH-7),
 * surfaced here rather than buried in settings because everything personal later resolves
 * through it.
 *
 * The mention list is empty until tagging fills it (INS-2). The header has room for two links
 * out: the person dossier (UI-8) and the World view page (UI-10).
 *
 * The entity is a page as well as a record (WORLD-9): its description is the page's
 * About section, the generated paragraph shows where there is no description, and the page's
 * beliefs and relations take the owner's hand here as on every other page (UI-20). A rename
 * does not move the page — its address is permanent (WORLD-3) — and the rename form says so.
 */
export function EntityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { api, entities, refreshEntities } = useApp();
  const navigate = useNavigate();

  const [entity, setEntity] = useState<Entity | null>(null);
  const [mentions, setMentions] = useState<EntityMention[]>([]);
  const [dossier, setDossier] = useState<EntityDossier | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    // Navigating entity → entity keeps this page mounted; without a reset the previous
    // entity's name and aliases flash until the new read lands.
    setEntity(null);
    setMentions([]);
    setDossier(null);
    setProblem(null);

    void api
      .getEntity(id)
      .then((detail) => {
        if (cancelled) return;
        setEntity(detail.entity);
        setMentions(detail.mentions);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setProblem(cause instanceof ApiError ? cause.message : "The entity could not be read.");
      });

    // The page behind it, read on its own: an entity detail stands without it (a term has none).
    void Promise.resolve()
      .then(() => api.getEntityDossier(id))
      .then((read) => !cancelled && setDossier(read))
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  // Every mutation returns the resolved entity, so the page and the shared list never
  // disagree: set it here, and refresh the copy the manager and navigator read.
  const reloadPage = useCallback(async () => {
    if (!id) return;
    try {
      setDossier(await api.getEntityDossier(id));
    } catch {
      // The page is extra; the detail stands without it.
    }
  }, [api, id]);

  const apply = useCallback(
    (updated: Entity) => {
      setEntity(updated);
      refreshEntities();
      // The description is the page's About section, so the page moved with it.
      void reloadPage();
    },
    [refreshEntities, reloadPage],
  );

  if (problem) {
    return (
      <Frame>
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      </Frame>
    );
  }

  if (!entity) {
    return (
      <Frame>
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      </Frame>
    );
  }

  const Icon = entityIcon(entity.type);

  return (
    <Frame>
      <Link
        to="/entities"
        className="inline-flex w-fit items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
      >
        <ArrowLeft size={14} strokeWidth={2} />
        Entities
      </Link>

      <div className="flex items-start gap-[var(--space-4)]">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-2)] text-[var(--vl-virtual-soft)]">
          <Icon size={20} strokeWidth={2} aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <NameHeader entity={entity} slug={dossier?.page ? dossier.slug : null} onSaved={apply} />
          <p className="mt-1 flex items-center gap-2.5 text-[length:var(--text-xs)] text-[var(--text-muted)]">
            <span>
              {entity.mentionCount} {entity.mentionCount === 1 ? "mention" : "mentions"} across sources
            </span>
            {/* The room UI-11 kept for it: for a person the dossier now exists (UI-8, M6.2),
                so the link lights up in place. The world view subject (UI-10) is still
                M7.2. */}
            {entity.isPerson && (
              <Link
                to={`/people/${entity.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] px-2 py-0.5 text-[length:var(--text-2xs)] text-[var(--vl-bridge-soft)] hover:border-[var(--border-strong)]"
              >
                <UsersRound size={12} strokeWidth={2} />
                Dossier
              </Link>
            )}

            {/* WORLD-9 — every entity but a term has a page from the moment it exists. */}
            {dossier?.page && (
              <Link
                to={`/world-view/${dossier.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] px-2 py-0.5 text-[length:var(--text-2xs)] text-[var(--vl-bridge-soft)] hover:border-[var(--border-strong)]"
              >
                <BookOpenText size={12} strokeWidth={2} />
                World view page
              </Link>
            )}

            {/* UI-19 — an initiative's own dossier, the same room kept for a person's. */}
            {entity.isInitiative && (
              <Link
                to={`/initiatives/${entity.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] px-2 py-0.5 text-[length:var(--text-2xs)] text-[var(--vl-bridge-soft)] hover:border-[var(--border-strong)]"
              >
                <FolderKanban size={12} strokeWidth={2} />
                Dossier
              </Link>
            )}
          </p>
        </div>

        {/* SPCH-7 lives here, in the header, not in settings — the milestone is explicit
            that the "is me" link is load-bearing and should be prominent. */}
        {entity.isPerson && <OwnerControl entity={entity} onSaved={apply} />}
      </div>

      <Description
        entity={entity}
        hasPage={Boolean(dossier?.page)}
        summary={dossier?.page?.summary ?? null}
        onSaved={apply}
      />
      <Aliases entity={entity} onSaved={apply} />

      {dossier?.page && (
        <>
          <RelationsPanel
            thisEntityId={entity.id}
            thisEntityName={entity.name}
            thisEntityKind={entity.type}
            relations={dossier.page.relations}
            editable
            candidates={entities}
            onChanged={reloadPage}
          />
          <BeliefsEditor
            slug={dossier.slug}
            sections={dossier.page.sections}
            onAssert={async (input) => {
              await api.assertBelief(entity.id, input);
              await reloadPage();
            }}
          />
        </>
      )}

      <Mentions mentions={mentions} />

      <RemoveControl
        entity={entity}
        onRemoved={() => {
          refreshEntities();
          navigate("/entities");
        }}
      />
    </Frame>
  );
}

// ------------------------------------------------------------------ name

function NameHeader({
  entity,
  slug,
  onSaved,
}: {
  entity: Entity;
  /** The page's permanent address, when there is a page. */
  slug: string | null;
  onSaved: (updated: Entity) => void;
}) {
  const { api } = useApp();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entity.name);
  const [busy, setBusy] = useState(false);

  async function save() {
    const name = draft.trim();
    if (name.length === 0 || busy) return;
    setBusy(true);
    try {
      onSaved(await api.updateEntity(entity.id, { name, description: entity.description ?? "" }));
      setEditing(false);
    } catch {
      // Kept editable; the draft is still on screen to retry.
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void save();
              if (event.key === "Escape") setEditing(false);
            }}
            aria-label="Entity name"
            autoFocus
            className="min-w-0 flex-1 rounded-[var(--radius-md)] border border-[var(--primary)] bg-[var(--surface-2)] px-3 py-1.5 text-[length:var(--text-xl)] font-bold text-[var(--text-primary)] outline-none"
          />
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="h-8 rounded-[var(--radius-md)] px-2 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy}
            className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] disabled:opacity-50"
          >
            <Check size={14} strokeWidth={2.5} />
            Save
          </button>
        </div>
        {/* WORLD-3 — a page's address is permanent, so a rename is the one edit that does not
            reach it. Said here, where the surprise would otherwise happen. */}
        {slug && (
          <p className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
            Renaming does not move its World view page: it stays at /world-view/{slug}, so every link to it keeps
            working.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <h1 className="text-[length:var(--text-2xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
        {entity.name}
      </h1>
      <button
        type="button"
        onClick={() => {
          setDraft(entity.name);
          setEditing(true);
        }}
        aria-label="Rename entity"
        className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] opacity-60 transition-opacity hover:opacity-100"
      >
        <PenLine size={12} strokeWidth={2} />
      </button>
      <span className="rounded-[var(--radius-full)] border border-[var(--border-subtle)] px-2 py-0.5 text-[length:var(--text-2xs)] text-[var(--text-secondary)]">
        {entityTypeLabel(entity.type)}
      </span>
      {entity.isMe && (
        <span className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold text-[var(--text-inverse)] [background:var(--vl-life)]">
          <UserCheck size={12} strokeWidth={2} />
          This is you
        </span>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ "is me"

function OwnerControl({ entity, onSaved }: { entity: Entity; onSaved: (updated: Entity) => void }) {
  const { api } = useApp();
  const [busy, setBusy] = useState(false);

  // When it already is you, the header badge says so; there is nothing to do here.
  if (entity.isMe) return null;

  async function claim() {
    if (busy) return;
    setBusy(true);
    try {
      onSaved(await api.setOwner(entity.id));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-[280px] shrink-0 items-center gap-[var(--space-3)]">
      <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
        Everything personal — commitments, prep, "my" perspective — resolves to whoever is marked as
        you.
      </span>
      <button
        type="button"
        onClick={() => void claim()}
        disabled={busy}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)] disabled:opacity-50"
      >
        <UserCheck size={14} strokeWidth={2} />
        This is me
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ description

function Description({
  entity,
  hasPage,
  summary,
  onSaved,
}: {
  entity: Entity;
  /** Whether the entity has a World view page to hold a generated paragraph (a term has none). */
  hasPage: boolean;
  /** WORLD-9 — the page's generated paragraph, shown only while there is no description. */
  summary: string | null;
  onSaved: (updated: Entity) => void;
}) {
  const { api } = useApp();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entity.description ?? "");
  const [busy, setBusy] = useState(false);
  const [asked, setAsked] = useState<"idle" | "queued" | "failed">("idle");

  // WORLD-9 — `summarize_subject` for any kind, as a person's dossier asks for it. Queued on an
  // org run, so the paragraph lands whenever placement's Reasoner gets to it; the page shows it
  // on the next read rather than this form polling for it.
  async function askForSummary() {
    try {
      await api.refreshEntitySummary(entity.id);
      setAsked("queued");
    } catch {
      setAsked("failed");
    }
  }

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      onSaved(await api.updateEntity(entity.id, { name: entity.name, description: draft.trim() }));
      setEditing(false);
    } catch {
      // Kept editable; the draft is still on screen to retry.
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)] backdrop-blur-[var(--blur-sm)]">
      {editing ? (
        <div className="flex flex-col gap-2.5">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={3}
            aria-label="Entity description"
            autoFocus
            className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-3 py-2 text-[length:var(--text-sm)] leading-relaxed text-[var(--text-primary)] outline-none"
          />
          <div className="flex items-center gap-2">
            <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
              This is the About section of its World view page, searchable as soon as it is saved, and every
              pipeline step reads it.
            </span>
            <span className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="h-8 rounded-[var(--radius-md)] px-2 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={busy}
                className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] disabled:opacity-50"
              >
                <Check size={14} strokeWidth={2.5} />
                Save
              </button>
            </span>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-[var(--space-4)]">
          <div className="min-w-0 flex-1">
            {entity.description ? (
              <p className="whitespace-pre-line text-[length:var(--text-sm)] leading-relaxed text-[var(--text-secondary)]">
                {entity.description}
              </p>
            ) : summary ? (
              <>
                <p className="text-[length:var(--text-sm)] leading-relaxed text-[var(--text-secondary)]">{summary}</p>
                <p className="mt-1.5 text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                  Generated from approved insights. A description you write is shown instead, and is never
                  overwritten.
                </p>
              </>
            ) : (
              <p className="text-[length:var(--text-sm)] leading-relaxed text-[var(--text-secondary)]">No description yet.</p>
            )}

            {!entity.description && hasPage && (
              <div className="mt-2 flex items-center gap-2 text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                {asked === "queued" ? (
                  <span>Asked for. The paragraph appears here once the Reasoner has written it.</span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => void askForSummary()}
                      className="inline-flex items-center gap-1.5 rounded-full border border-transparent px-2 py-0.5 text-[var(--vl-bridge-soft)] hover:border-[var(--border-subtle)]"
                    >
                      <Sparkles size={12} strokeWidth={2} />
                      {summary ? "Rewrite the summary" : "Write a summary from sources"}
                    </button>
                    {asked === "failed" && (
                      <span role="alert" className="text-[var(--warning)]">
                        The summary could not be asked for.
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setDraft(entity.description ?? "");
              setEditing(true);
            }}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-[var(--radius-full)] px-2 py-1 text-[length:var(--text-2xs)] text-[var(--text-muted)] opacity-60 transition-opacity hover:opacity-100"
          >
            <PenLine size={12} strokeWidth={2} />
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ aliases

function Aliases({ entity, onSaved }: { entity: Entity; onSaved: (updated: Entity) => void }) {
  const { api } = useApp();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  async function set(next: string[]) {
    try {
      onSaved(await api.setEntityAliases(entity.id, next));
      setProblem(null);
    } catch (cause: unknown) {
      // Without this, a failed save is a chip that silently refuses to change.
      setProblem(cause instanceof ApiError ? cause.message : "The aliases could not be saved.");
    }
  }

  async function commit() {
    const alias = draft.trim();
    setAdding(false);
    setDraft("");
    if (alias.length === 0) return;
    if (entity.aliases.some((existing) => existing.toLowerCase() === alias.toLowerCase())) return;
    await set([...entity.aliases, alias]);
  }

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-2.5">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">Aliases</h2>
        <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Spellings and shorthands that resolve to this entity.
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {entity.aliases.map((alias) => (
          <span
            key={alias}
            className="inline-flex items-center gap-1 rounded-[var(--radius-full)] bg-[var(--secondary)] py-1 pl-2.5 pr-1.5 text-[length:var(--text-xs)] text-[var(--secondary-foreground)]"
          >
            {alias}
            <button
              type="button"
              onClick={() => void set(entity.aliases.filter((existing) => existing !== alias))}
              aria-label={`Remove alias ${alias}`}
              className="flex h-4 w-4 items-center justify-center rounded-[var(--radius-full)] text-[var(--text-muted)] opacity-60 transition-[opacity,color] hover:text-[var(--destructive)] hover:opacity-100"
            >
              <X size={11} strokeWidth={2.5} />
            </button>
          </span>
        ))}

        {adding ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void commit();
              if (event.key === "Escape") {
                setAdding(false);
                setDraft("");
              }
            }}
            onBlur={() => void commit()}
            placeholder="Alias, then Enter"
            aria-label="New alias"
            className="w-[170px] rounded-[var(--radius-full)] border border-[var(--primary)] bg-[var(--surface-2)] px-2.5 py-1 text-[length:var(--text-xs)] text-[var(--text-primary)] outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] border border-dashed border-[var(--border-default)] px-2.5 py-1 text-[length:var(--text-xs)] text-[var(--text-muted)] transition-colors hover:border-[var(--primary)] hover:text-[var(--vl-bridge-soft)]"
          >
            <Plus size={12} strokeWidth={2} />
            Add alias
          </button>
        )}
      </div>

      {problem && (
        <p role="alert" className="mt-2 text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ mentions

function Mentions({ mentions }: { mentions: EntityMention[] }) {
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-2.5">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">Mentions</h2>
        <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {mentions.length > 0
            ? `Every place this entity was named, newest first.`
            : `Where this entity is named across your sources.`}
        </span>
      </div>

      {mentions.length === 0 ? (
        <p className="rounded-[var(--radius-md)] border border-dashed border-[var(--border-subtle)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          No mentions yet. They appear once tagging reads your sources, which arrives in M5.1.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {mentions.map((mention, index) => (
            <MentionRow key={`${mention.sourceMaterialId}-${index}`} mention={mention} />
          ))}
        </div>
      )}
    </section>
  );
}

function MentionRow({ mention }: { mention: EntityMention }) {
  const Icon = mention.sourceKind === "recording" ? FileAudio : FileText;
  const body = (
    <>
      <div className="flex items-center gap-2 text-[length:var(--text-sm)]">
        <Icon size={12} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-[var(--vl-bridge-soft)]">
          {mention.sourceTitle ?? "Untitled source"}
        </span>
        <span className="shrink-0 text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          {formatDate(mention.createdAt)}
        </span>
      </div>
      <p className="text-[length:var(--text-xs)] italic leading-relaxed text-[var(--text-secondary)]">
        {mention.quote}
      </p>
    </>
  );

  // A recording has a detail page to open; a document does not have one yet.
  const className =
    "flex flex-col gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-2.5 transition-[border-color,box-shadow]";

  return mention.sourceKind === "recording" ? (
    <Link to={`/recordings/${mention.sourceMaterialId}`} className={cn(className, "hover:border-[var(--border-strong)] hover:shadow-[var(--shadow-md)]")}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

// ------------------------------------------------------------------ remove

function RemoveControl({ entity, onRemoved }: { entity: Entity; onRemoved: () => void }) {
  const { api } = useApp();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function remove() {
    if (busy) return;
    setBusy(true);
    try {
      await api.deleteEntity(entity.id);
      onRemoved();
    } catch {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div className="mt-[var(--space-2)] flex items-center justify-end gap-[var(--space-3)] border-t border-[var(--border-subtle)] pt-[var(--space-4)]">
      {confirming ? (
        <>
          <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
            Remove this entity and its aliases and mentions? The sources are untouched.
          </span>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            Keep it
          </button>
          <button
            type="button"
            onClick={() => void remove()}
            disabled={busy}
            className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--destructive)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--text-inverse)] transition hover:brightness-110 disabled:opacity-50"
          >
            <Trash2 size={14} strokeWidth={2} />
            {busy ? "Removing…" : "Remove"}
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] px-3 text-[length:var(--text-sm)] text-[var(--text-muted)] transition-colors hover:text-[var(--destructive)]"
        >
          <Trash2 size={14} strokeWidth={2} />
          Remove entity
        </button>
      )}
    </div>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-[var(--space-4)] px-[var(--space-8)] py-[var(--space-8)]">{children}</div>
  );
}
